/**
 * Dossier patient : identité déclarée, mode de vie, antécédents et liens familiaux.
 *
 * Ensembles fermés partagés entre la base (pgEnum), le serveur (zod) et le front (libellés i18n).
 * Règle : valeurs ASCII en snake_case, jamais de libellé humain ici. Ce fichier est réexporté par
 * `../enums.js`, qui reste le seul point d'entrée : personne n'importe un domaine directement.
 */

/** V1 ne connaissait que male/female ; l'écran de pré-inscription propose déjà M/F/X. */
export const patientSexes = ['male', 'female', 'other'] as const;
export type PatientSex = (typeof patientSexes)[number];

export const referralSources = [
  'bouche_a_oreille',
  'medecin',
  'internet',
  'reseaux_sociaux',
  'autre',
] as const;
export type ReferralSource = (typeof referralSources)[number];

export const familyStatuses = [
  'celibataire',
  'marie',
  'pacse',
  'divorce',
  'veuf',
  'non_renseigne',
] as const;
export type FamilyStatus = (typeof familyStatuses)[number];

export const noteImportances = ['bas', 'moyen', 'haut'] as const;
export type NoteImportance = (typeof noteImportances)[number];

export const handPreferences = ['droitier', 'gaucher', 'ambidextre'] as const;
export type HandPreference = (typeof handPreferences)[number];

export const smokerStatuses = ['non', 'oui', 'ancien'] as const;
export type SmokerStatus = (typeof smokerStatuses)[number];

export const alcoholConsumptions = ['non', 'occasionnelle', 'reguliere'] as const;
export type AlcoholConsumption = (typeof alcoholConsumptions)[number];

export const quantityPeriods = ['par_jour', 'par_semaine'] as const;
export type QuantityPeriod = (typeof quantityPeriods)[number];

/** Une seule table d'historique remplace `antecedents` et `spheres` de la V1. */
export const patientHistoryKinds = ['antecedent', 'sphere'] as const;
export type PatientHistoryKind = (typeof patientHistoryKinds)[number];

/** Catégories d'antécédent de la V1, translittérées en ASCII snake_case. */
export const patientHistoryCategories = [
  'chirurgical',
  'traumatique',
  'medical',
  'familial',
  'psychologique',
  'allergique',
  'autre',
] as const;
export type PatientHistoryCategory = (typeof patientHistoryCategories)[number];

/** Systèmes des sphères de la V1, en ASCII, plus `psychologique` que la spécification des écrans exige. */
export const patientHistorySystems = [
  'cardiovasculaire',
  'dentaire',
  'dermatologique',
  'digestif',
  'endocrinien',
  'genital',
  'neurologique',
  'ophtalmologique',
  'orl',
  'orthopedique',
  'psychologique',
  'respiratoire',
  'urinaire',
  'autre',
] as const;
export type PatientHistorySystem = (typeof patientHistorySystems)[number];

export const patientHistoryPeriodicities = ['ponctuel', 'periodique'] as const;
export type PatientHistoryPeriodicity = (typeof patientHistoryPeriodicities)[number];

/**
 * Repère de la date d'une sphère, devant le nombre.
 * `ago` : « il y a 15 mois ». `at_age` : « à l'âge de 15 ans ».
 * `year` : « en 2015 », antécédent seulement ; le nombre est l'année, sans unité.
 * La V1 ne demandait que la valeur et l'unité : l'absence de choix se lit comme `ago`.
 */
export const patientHistoryDateReferences = ['ago', 'at_age', 'year'] as const;
export type PatientHistoryDateReference = (typeof patientHistoryDateReferences)[number];

/** Unité du nombre qui suit le repère, ou d'une durée d'activité. */
export const patientHistoryDateUnits = ['days', 'months', 'years'] as const;
export type PatientHistoryDateUnit = (typeof patientHistoryDateUnits)[number];

const STORED_DURATION = /^(\d{1,5}):(days|months|years)$/;

/** Durée d'activité stockée sans langue : `15:months`. Un texte libre ancien ne se décode pas. */
export function encodeHistoryDuration(amount: number, unit: PatientHistoryDateUnit): string {
  return `${String(amount)}:${unit}`;
}

export function decodeHistoryDuration(
  value: string | null,
): { amount: number; unit: PatientHistoryDateUnit } | null {
  if (value === null) return null;
  const match = STORED_DURATION.exec(value);
  const amount = match?.[1];
  const unit = match?.[2];
  if (amount === undefined || (unit !== 'days' && unit !== 'months' && unit !== 'years')) {
    return null;
  }
  return { amount: Number(amount), unit };
}

export const patientRelationshipTypes = [
  'conjoint',
  'parent',
  'enfant',
  'fratrie',
  'grand_parent',
  'petit_enfant',
  'oncle_tante',
  'neveu_niece',
  'cousin',
  'autre',
] as const;
export type PatientRelationshipType = (typeof patientRelationshipTypes)[number];

/** Lien inverse créé automatiquement, comme en V1 (`createPatientRelationship`). */
export const inversePatientRelationshipType: Record<
  PatientRelationshipType,
  PatientRelationshipType
> = {
  conjoint: 'conjoint',
  parent: 'enfant',
  enfant: 'parent',
  fratrie: 'fratrie',
  grand_parent: 'petit_enfant',
  petit_enfant: 'grand_parent',
  oncle_tante: 'neveu_niece',
  neveu_niece: 'oncle_tante',
  cousin: 'cousin',
  autre: 'autre',
};

/**
 * Pays qui délivre le numéro de sécurité sociale du patient — CNS, NIR, NISS — ou « autre »,
 * texte libre. Distinct de `countries` (le périmètre des cabinets, Luxembourg seul) : la
 * patientèle d'un cabinet luxembourgeois est frontalière, et la V1 offrait déjà ce choix devant
 * le champ. `OTHER` n'est pas vérifié, il est gardé tel quel.
 */
export const ssnCountries = ['LU', 'FR', 'BE', 'OTHER'] as const;
export type SsnCountry = (typeof ssnCountries)[number];
